import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { inboundWebhookUrl, slugFromDisplayName } from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import type { Project } from '@/api/types';
import { useProjectContext } from '@/project/ProjectContext';
import { ProjectDot } from '@/layout/ProjectSwitcher';
import { Button } from '@/components/ui/Button';
import { Field, TextInput, TextArea } from '@/components/ui/Field';
import { ErrorState, LoadingState, StateMessage } from '@/components/StateMessage/StateMessage';
import { projectErrorMessage } from '@/features/projects/projectErrors';
import {
  normalizeWebsiteToDomain,
  websiteHref,
  websiteLabel,
} from '@/features/projects/websiteUrl';
import styles from './ProjectsPage.module.css';

const defaultCreate = {
  slug: '',
  name: '',
  domain: '',
  color: '#3B5BFF',
  from_name: '',
  signature_html: '',
  allowed_origins: '',
};

function originsToLines(origins: string[] | null | undefined): string {
  return (origins ?? []).join('\n');
}

function linesToOrigins(text: string): string[] {
  return text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

export function ProjectsPage() {
  const { projects, projectsLoading, setSelectedProjectId, selectedProjectId } =
    useProjectContext();
  const qc = useQueryClient();
  const apiOrigin = typeof window !== 'undefined' ? window.location.origin : '';

  const [editing, setEditing] = useState<Project | null>(null);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState(defaultCreate);
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const [deleteConfirmSlug, setDeleteConfirmSlug] = useState('');

  const createProject = useMutation({
    mutationFn: () =>
      apiFetch<{ project: Project; inboundApiKey: string }>('/api/projects', {
        method: 'POST',
        body: JSON.stringify({
          slug: createForm.slug,
          name: createForm.name,
          domain: normalizeWebsiteToDomain(createForm.domain),
          color: createForm.color,
          from_name: createForm.from_name || null,
          signature_html: createForm.signature_html || null,
          allowed_origins: linesToOrigins(createForm.allowed_origins),
        }),
      }),
    onSuccess: (res) => {
      setNewKey(res.inboundApiKey);
      setSelectedProjectId(res.project.id);
      void qc.invalidateQueries({ queryKey: ['projects'] });
      setCreateForm(defaultCreate);
    },
  });

  const saveProject = useMutation({
    mutationFn: (p: Project) =>
      apiFetch(`/api/projects/${p.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: p.name,
          domain: p.domain,
          color: p.color,
          from_name: p.from_name,
          signature_html: p.signature_html,
          allowed_origins: p.allowed_origins ?? [],
          lifecycle_status: p.lifecycle_status ?? 'active',
          timezone: p.timezone ?? 'Europe/Amsterdam',
          locale: p.locale ?? 'nl-NL',
        }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      setEditing(null);
    },
  });

  function saveEditingProject(p: Project) {
    saveProject.mutate({
      ...p,
      domain: p.domain ? normalizeWebsiteToDomain(p.domain) : null,
    });
  }

  const deleteProject = useMutation({
    mutationFn: ({ id, confirmSlug }: { id: string; confirmSlug: string }) =>
      apiFetch(`/api/projects/${id}`, {
        method: 'DELETE',
        body: JSON.stringify({ confirmSlug }),
      }),
    onSuccess: (_data, vars) => {
      if (selectedProjectId === vars.id) {
        const remaining = projects.filter((p) => p.id !== vars.id);
        setSelectedProjectId(remaining[0]?.id ?? null);
      }
      void qc.invalidateQueries({ queryKey: ['projects'] });
      setDeleteTarget(null);
      setDeleteConfirmSlug('');
      if (editing?.id === vars.id) setEditing(null);
    },
  });

  const rotateKey = useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ inboundApiKey: string; inboundApiKeyPrefix?: string }>(
        `/api/projects/${id}/rotate-inbound-key`,
        { method: 'POST', body: JSON.stringify({}) },
      ),
    onSuccess: (res) => setNewKey(res.inboundApiKey),
  });

  function submitCreate(e: FormEvent) {
    e.preventDefault();
    createProject.mutate();
  }

  function generateSlugFromName() {
    setCreateForm((f) => ({ ...f, slug: slugFromDisplayName(f.name) }));
  }

  async function copyText(text: string) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  }

  if (projectsLoading) return <LoadingState />;

  return (
    <div className={`${styles.page} ${styles.grid}`}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.title}>Projectinstellingen</h1>
          <p className={styles.pageLead}>
            Beheer merken en workspaces: website, mail-defaults en inbound-webhooks. Slug is vast na
            aanmaken.{' '}
            <Link to="/app/leads">Terug naar leads</Link>
            {' · '}
            <Link to="/app/bedrijfs-dna">Bedrijfs-DNA</Link>
          </p>
        </div>
        <span className={styles.statPill}>
          {projects.length} {projects.length === 1 ? 'project' : 'projecten'}
        </span>
      </header>

      {newKey ? (
        <div className={styles.cardElevated} role="alert">
          <h2 className={styles.cardTitle}>Nieuwe inbound API-key (één keer zichtbaar)</h2>
          <p className={styles.pageLead}>Kopieer en bewaar deze key offline. We tonen hem niet opnieuw.</p>
          <div className={styles.keyBox}>{newKey}</div>
          <Button onClick={() => setNewKey(null)}>Ik heb de key opgeslagen</Button>
        </div>
      ) : null}

      <div className={styles.layoutSplit}>
        <form className={styles.cardElevated} onSubmit={(e) => void submitCreate(e)}>
          <h2 className={styles.cardTitle}>Nieuw project</h2>
          <div className={styles.section}>
            <p className={styles.sectionTitle}>Identiteit</p>
            <div className={styles.formGridWide}>
              <div className={styles.span2}>
                <Field label="Naam" hint="Zichtbaar in de switcher.">
                  <TextInput
                    required
                    value={createForm.name}
                    onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </Field>
              </div>
              <div className={styles.span2}>
                <Field
                  label="Website-URL"
                  hint="Volledige URL of domein — gebruikt o.a. voor Bedrijfs-DNA scan."
                >
                  <TextInput
                    type="url"
                    inputMode="url"
                    value={createForm.domain}
                    onChange={(e) => setCreateForm((f) => ({ ...f, domain: e.target.value }))}
                    placeholder="https://www.voorbeeld.nl"
                  />
                </Field>
              </div>
              <div className={styles.span2}>
                <Field
                  label="Slug"
                  hint="Inbound webhook-pad. Alleen a-z, 0-9, koppeltekens."
                >
                <div className={styles.row}>
                  <TextInput
                    required
                    value={createForm.slug}
                    onChange={(e) => setCreateForm((f) => ({ ...f, slug: e.target.value }))}
                    pattern="[a-z0-9]+(-[a-z0-9]+)*"
                  />
                  <Button type="button" variant="ghost" onClick={generateSlugFromName}>
                    Uit naam
                  </Button>
                </div>
                </Field>
              </div>
              <Field label="Accentkleur">
                <div className={styles.colorField}>
                  <input
                    type="color"
                    value={createForm.color}
                    onChange={(e) => setCreateForm((f) => ({ ...f, color: e.target.value }))}
                    aria-label="Projectkleur"
                  />
                  <TextInput
                    value={createForm.color}
                    onChange={(e) => setCreateForm((f) => ({ ...f, color: e.target.value }))}
                  />
                </div>
              </Field>
            </div>
          </div>
          <div className={styles.section}>
            <p className={styles.sectionTitle}>Mail (optioneel)</p>
            <div className={styles.formGrid}>
              <Field label="Afzendernaam">
                <TextInput
                  value={createForm.from_name}
                  onChange={(e) => setCreateForm((f) => ({ ...f, from_name: e.target.value }))}
                />
              </Field>
              <Field label="Handtekening (HTML)">
                <TextArea
                  rows={3}
                  value={createForm.signature_html}
                  onChange={(e) => setCreateForm((f) => ({ ...f, signature_html: e.target.value }))}
                />
              </Field>
              <Field label="CORS origins" hint="Één URL per regel.">
                <TextArea
                  rows={2}
                  value={createForm.allowed_origins}
                  onChange={(e) => setCreateForm((f) => ({ ...f, allowed_origins: e.target.value }))}
                />
              </Field>
            </div>
          </div>
          {createProject.isError ? (
            <ErrorState message={projectErrorMessage(createProject.error)} />
          ) : null}
          <Button type="submit" disabled={createProject.isPending}>
            Project aanmaken
          </Button>
        </form>

        <div>
          {!projects.length ? (
            <StateMessage title="Geen projecten">
              Maak links je eerste workspace aan.
            </StateMessage>
          ) : (
            <div className={styles.projectPanel}>
              <div className={styles.projectPanelHead}>
                <span>Project</span>
                <span className={styles.colWebsite}>Website</span>
                <span>Acties</span>
              </div>
              {projects.map((p) => {
                const href = websiteHref(p.domain);
                const isSelected = p.id === selectedProjectId;
                return (
                  <div
                    key={p.id}
                    className={[styles.projectRow, isSelected ? styles.projectRowActive : ''].join(
                      ' ',
                    )}
                  >
                    <div className={styles.projectIdentity}>
                      <ProjectDot color={p.color} />
                      <div style={{ minWidth: 0 }}>
                        <div className={styles.projectName}>{p.name}</div>
                        <small className={styles.meta}>
                          {p.slug}
                          {p.lifecycle_status === 'archived' ? (
                            <span className={styles.badgeArchived}> Gearchiveerd</span>
                          ) : (
                            <span className={styles.badgeActive}> Actief</span>
                          )}
                        </small>
                      </div>
                    </div>
                    <div className={styles.colWebsite}>
                      {href ? (
                        <a
                          className={styles.websiteLink}
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {websiteLabel(p.domain)}
                        </a>
                      ) : (
                        <span className={styles.websiteEmpty}>Geen website</span>
                      )}
                    </div>
                    <div className={styles.rowActions}>
                      <Button variant="secondary" onClick={() => setEditing({ ...p })}>
                        Bewerken
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          if (
                            window.confirm(
                              'Inbound API-key roteren? Oude key blijft tijdelijk geldig.',
                            )
                          ) {
                            rotateKey.mutate(p.id);
                          }
                        }}
                      >
                        Key
                      </Button>
                      <Button variant="danger" onClick={() => setDeleteTarget(p)}>
                        Verwijder
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {editing ? (
        <form
          className={`${styles.cardElevated} ${styles.editOverlay}`}
          onSubmit={(e) => {
            e.preventDefault();
            saveEditingProject(editing);
          }}
        >
          <h2 className={styles.cardTitle}>Bewerken — {editing.name}</h2>
          <div className={styles.formGridWide}>
            <div className={styles.section}>
              <p className={styles.sectionTitle}>Identiteit & website</p>
              <div className={styles.formGridWide}>
                <Field label="Naam">
                  <TextInput
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    required
                  />
                </Field>
                <Field label="Website-URL" hint="https://… of alleen domein.">
                  <TextInput
                    type="url"
                    inputMode="url"
                    value={editing.domain ?? ''}
                    placeholder="https://www.voorbeeld.nl"
                    onChange={(e) => setEditing({ ...editing, domain: e.target.value || null })}
                  />
                </Field>
                <Field label="Slug (vast)">
                  <TextInput value={editing.slug} readOnly disabled />
                </Field>
                <Field label="Accentkleur">
                  <div className={styles.colorField}>
                    <input
                      type="color"
                      value={editing.color ?? '#3B5BFF'}
                      onChange={(e) => setEditing({ ...editing, color: e.target.value })}
                      aria-label="Projectkleur"
                    />
                    <TextInput
                      value={editing.color ?? '#3B5BFF'}
                      onChange={(e) => setEditing({ ...editing, color: e.target.value })}
                    />
                  </div>
                </Field>
              </div>
            </div>
            <div className={styles.section}>
              <p className={styles.sectionTitle}>Integratie</p>
              <Field label="Inbound webhook" hint="POST voor lead-capture.">
                <div className={styles.keyBox}>{inboundWebhookUrl(apiOrigin, editing.slug)}</div>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => void copyText(inboundWebhookUrl(apiOrigin, editing.slug))}
                >
                  URL kopiëren
                </Button>
              </Field>
              {editing.inbound_api_key_prefix ? (
                <Field label="Inbound key-prefix">
                  <TextInput value={editing.inbound_api_key_prefix} readOnly disabled />
                </Field>
              ) : null}
            </div>
            <div className={styles.section}>
              <p className={styles.sectionTitle}>Mail & regio</p>
              <div className={styles.formGridWide}>
                <Field label="Afzendernaam">
                  <TextInput
                    value={editing.from_name ?? ''}
                    onChange={(e) => setEditing({ ...editing, from_name: e.target.value || null })}
                  />
                </Field>
                <Field label="Status">
                  <select
                    className={styles.filterInput}
                    value={editing.lifecycle_status ?? 'active'}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        lifecycle_status: e.target.value as 'active' | 'archived',
                      })
                    }
                  >
                    <option value="active">Actief</option>
                    <option value="archived">Gearchiveerd</option>
                  </select>
                </Field>
                <Field label="Tijdzone">
                  <TextInput
                    value={editing.timezone ?? 'Europe/Amsterdam'}
                    onChange={(e) => setEditing({ ...editing, timezone: e.target.value })}
                  />
                </Field>
                <Field label="Locale">
                  <TextInput
                    value={editing.locale ?? 'nl-NL'}
                    onChange={(e) => setEditing({ ...editing, locale: e.target.value })}
                  />
                </Field>
                <div className={styles.span2}>
                  <Field label="Handtekening (HTML)">
                    <TextArea
                      rows={4}
                      value={editing.signature_html ?? ''}
                      onChange={(e) =>
                        setEditing({ ...editing, signature_html: e.target.value || null })
                      }
                    />
                  </Field>
                </div>
                <div className={styles.span2}>
                  <Field label="CORS origins" hint="Één URL per regel.">
                    <TextArea
                      value={originsToLines(editing.allowed_origins)}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          allowed_origins: linesToOrigins(e.target.value),
                        })
                      }
                    />
                  </Field>
                </div>
              </div>
            </div>
          </div>
          {saveProject.isError ? (
            <ErrorState message={projectErrorMessage(saveProject.error)} />
          ) : null}
          <div className={styles.row}>
            <Button type="submit" disabled={saveProject.isPending}>
              Wijzigingen opslaan
            </Button>
            <Button type="button" variant="ghost" onClick={() => setEditing(null)}>
              Annuleren
            </Button>
          </div>
        </form>
      ) : null}

      {deleteTarget ? (
        <div className={styles.card} role="dialog" aria-labelledby="delete-project-title">
          <h2 id="delete-project-title">Project verwijderen</h2>
          <p>
            Alles van <strong>{deleteTarget.name}</strong> wordt permanent verwijderd: leads,
            mailboxen, campagnes en mail. Dit kan niet ongedaan.
          </p>
          <p>
            Typ de slug <code className={styles.inlineCode}>{deleteTarget.slug}</code> om te
            bevestigen.
          </p>
          <Field label="Bevestig slug">
            <TextInput
              value={deleteConfirmSlug}
              onChange={(e) => setDeleteConfirmSlug(e.target.value)}
              autoComplete="off"
            />
          </Field>
          {deleteProject.isError ? (
            <ErrorState message={projectErrorMessage(deleteProject.error)} />
          ) : null}
          <div className={styles.row}>
            <Button
              variant="danger"
              disabled={deleteProject.isPending || deleteConfirmSlug !== deleteTarget.slug}
              onClick={() =>
                deleteProject.mutate({
                  id: deleteTarget.id,
                  confirmSlug: deleteConfirmSlug,
                })
              }
            >
              Definitief verwijderen
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setDeleteTarget(null);
                setDeleteConfirmSlug('');
              }}
            >
              Annuleren
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
